// @flow
import React, {useContext, useEffect, useState} from "react";
import GuiUtils from "../GUI_COMMON/SUPPORT/GuiUtils";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import type {BTOBox, BTOBoxAndMagsItem} from "./BTOAddUpdatePage";
import {boxIsGhostBox} from "./BTOPickSheetPage";

interface BoxesListProps {
    name: string,
    boxesAndMags: BTOBoxAndMagsItem[],
    magType: number,
    onBoxClicked: (boxWithMags: BTOBoxAndMagsItem) => void,
    onCheckChanged?: (item: BTOBoxAndMagsItem, checked: boolean) => void,
    onOrderClicked: (btoOrderId: number) => void,
    onBoxBadgeClicked: (box: BTOBox, badgeText: string) => void,
    showCheckBox?: boolean,
    showDelete?: boolean,
    onDeleteClicked: (box: BTOBox) => void,
    readOnly: boolean,
    isLoading: boolean,
    thisOrderId: number,
    returnNullOnNoneFound?: boolean,
    newlyAddedBoxIds?: Set<number>
}

const BoxesList: React.FC<BoxesListProps> = ({name, boxesAndMags, magType, onBoxClicked,
                                                 onCheckChanged, onOrderClicked, onBoxBadgeClicked,
                                                 showCheckBox, showDelete, onDeleteClicked, readOnly, 
                                                 isLoading, thisOrderId=undefined, 
                                                returnNullOnNoneFound=false, newlyAddedBoxIds}) => {

    const [globalState] = useContext(StoreContext);
    const [filteredBoxes, setFilteredBoxes] = useState([]);

    useEffect(() => { // we only want our own mag type listed here
        if(!boxesAndMags) return;
        const sList = boxesAndMags.filter(boxWithMags => boxWithMags.box.holdsMagType === magType)??[];
        setFilteredBoxes(sList);
    }, [boxesAndMags]); // eslint-disable-line react-hooks/exhaustive-deps
    
    function handleDeleteClicked(item:BTOBox) {
        if (onDeleteClicked) {
            onDeleteClicked(item);
        }
    }
    
    function dumpBoxMagNumbers(childMags) {
        
        let magSpan;
        if(!childMags || childMags.length===0) {
            magSpan = "no mags";
        }
        else {
            magSpan = `${childMags.length} mag${childMags.length===1?"":"s"}`;
        }
        return (
            <span className="pl-1 li-fg-slightly-muted">
                [{magSpan}]
            </span>
        );
    }

    function dumpBoxRow(item:BTOBoxAndMagsItem) {
        const box = item.box;
        const boxStr = box.barcode;

        const isNewlyAdded = newlyAddedBoxIds && newlyAddedBoxIds.has(box.id);
        const animationClass = isNewlyAdded ? ' li-box-item-flash' : '';
        
        return (
            <div key={box.id} className={`row p-0${animationClass}`} style={{marginBottom:"4px"}}>
                {showCheckBox &&
                <input type="checkbox" className="m-2 mr-1" name={name} data-parent-id={box.id} aria-label="Checkbox"
                       disabled={readOnly}
                       onChange={(e) => onCheckChanged?.(item, e.target.checked)}/>
                }
                <div className="align-self-center text-nowrap">
                    {showDelete && !readOnly &&
                        <span className="m-2 mr-1 li-pointer" data-toggle="tooltip" title="Delete this box"
                              onClick={() => handleDeleteClicked(item)}>
                                <i className="fa fa-lg fa-times-circle li-fg-danger"/></span>
                    }
                    <div className="d-inline-block">
                        <span className="li-pointer btn-link" onClick={() => onBoxClicked(item)}>{LIUtils.SanitizeBarcodeToLength(boxStr)}</span>
                        {dumpBoxMagNumbers(item.childMags)}
                    </div>
                    {!boxIsGhostBox(box) && ArchiverGuiUtils.ShowBoxIsPendingPickedOrShipped(
                        box,onOrderClicked,canSetBoxReference(item)?onBoxBadgeClicked:null,true,thisOrderId)}
                </div>
            </div>
        ); 
    }
    
    function canSetBoxReference(item:BTOBoxAndMagsItem) {
        // we can allow the user to set the box reference if the
        // order has been saved (meaning the box is actually on the order at the archiver and can have it's
        // reference updated immediately).
        return thisOrderId>0 && item.box.parentOrderId===thisOrderId;
    }

    if (!filteredBoxes || filteredBoxes.length === 0) {
        if(returnNullOnNoneFound) return null;
        
        const magTypeName =GuiUtils.GetMagTypeNameGivenId(magType,globalState.magTypes.items);
        return <div className="ml-5">no {magTypeName} found</div>;
    }
    
    return (
        <div className="row row-cols-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2 m-0 p-0 position-static">
            {filteredBoxes.map((item) => dumpBoxRow(item))}
        </div>
    );
}

/* this is used by external queriers if they need to determine if anything 
       will be returned by this component or not (### kind of hacky)
       Returns: null if nothing will be returned, otherwise the filtered boxes for the given mag type
     */
export function BoxesListWillReturnNothing(boxAndMagList:BTOBoxAndMagsItem[], magType:number) {
    const filteredBoxes = boxAndMagList?.filter(item => item.box.holdsMagType === magType);
    return filteredBoxes?.length === 0;
}

export default BoxesList;